/**
 * On-screen virtual joystick for touch devices. Bottom-left overlay. The
 * knob drags within a circular deadzone and emits a normalized 2D vector
 * (x, y), each in [-1, 1], magnitude ≤ 1. Y is inverted so "up" on the
 * joystick = forward (-y in screen coords → +forward in world).
 *
 * Visibility is CSS-driven: the #touch-joystick element is hidden by default
 * and shown via @media (hover: none) and (pointer: coarse). This module
 * still wires up event listeners unconditionally — no-op on devices that
 * never fire touch events.
 */
export class TouchJoystick {
  private base: HTMLElement;
  private knob: HTMLElement;
  private onMove: (x: number, y: number) => void;
  private touchId: number | null = null;
  private radius: number = 50;

  constructor(
    baseId: string,
    knobId: string,
    onMove: (x: number, y: number) => void
  ) {
    const base: HTMLElement | null = document.getElementById(baseId);
    const knob: HTMLElement | null = document.getElementById(knobId);
    if (base === null || knob === null) {
      throw new Error(`TouchJoystick elements ${baseId}/${knobId} not found`);
    }
    this.base = base;
    this.knob = knob;
    this.onMove = onMove;

    this.base.addEventListener("touchstart", this.handleStart, { passive: false });
    this.base.addEventListener("touchmove", this.handleMove, { passive: false });
    this.base.addEventListener("touchend", this.handleEnd, { passive: false });
    this.base.addEventListener("touchcancel", this.handleEnd, { passive: false });
  }

  private handleStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (this.touchId !== null) {
      return;
    }
    const t: Touch = e.changedTouches[0];
    this.touchId = t.identifier;
    const rect: DOMRect = this.base.getBoundingClientRect();
    this.radius = Math.min(rect.width, rect.height) / 2 - 30;
    this.update(t.clientX - rect.left - rect.width / 2, t.clientY - rect.top - rect.height / 2);
  };

  private handleMove = (e: TouchEvent): void => {
    e.preventDefault();
    const t: Touch | undefined = this.findTouch(e.changedTouches);
    if (t === undefined) {
      return;
    }
    const rect: DOMRect = this.base.getBoundingClientRect();
    this.update(t.clientX - rect.left - rect.width / 2, t.clientY - rect.top - rect.height / 2);
  };

  private handleEnd = (e: TouchEvent): void => {
    const t: Touch | undefined = this.findTouch(e.changedTouches);
    if (t === undefined) {
      return;
    }
    this.touchId = null;
    this.reset();
  };

  private findTouch(list: TouchList): Touch | undefined {
    for (let i: number = 0; i < list.length; i++) {
      if (list[i].identifier === this.touchId) {
        return list[i];
      }
    }
    return undefined;
  }

  private update(dx: number, dy: number): void {
    const dist: number = Math.sqrt(dx * dx + dy * dy);
    const clamped: number = Math.min(dist, this.radius);
    const angle: number = Math.atan2(dy, dx);
    const kx: number = Math.cos(angle) * clamped;
    const ky: number = Math.sin(angle) * clamped;
    this.knob.style.transform = `translate(calc(-50% + ${kx}px), calc(-50% + ${ky}px))`;

    // Normalize to [-1, 1]; invert Y so "up" = forward.
    const nx: number = this.radius === 0 ? 0 : kx / this.radius;
    const ny: number = this.radius === 0 ? 0 : -ky / this.radius;
    this.onMove(nx, ny);
  }

  private reset(): void {
    this.knob.style.transform = `translate(-50%, -50%)`;
    this.onMove(0, 0);
  }
}
